import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Sparkles, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function StatusBadge({ status }) {
  const map = {
    generating: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    ready: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    failed: "bg-red-500/10 text-red-400 border-red-500/20",
  };
  return <span className={`text-xs px-2 py-1 rounded border ${map[status] || "border-border"}`} data-testid={`status-${status}`}>{status}</span>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [lists, setLists] = useState(null);

  const load = async () => {
    const { data } = await api.get("/lead-lists");
    setLists(data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="overline text-primary">Dashboard</div>
            <h1 className="mt-2 text-4xl font-medium tracking-tighter">Your lead lists</h1>
            <p className="text-sm text-muted-foreground mt-2">AI-generated; verify before contacting.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-xl border border-border bg-[#121214] px-5 py-3" data-testid="credits-card">
              <div className="text-xs text-muted-foreground uppercase tracking-[0.18em]">Credits</div>
              <div className="mt-1 text-2xl font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />{user?.credits === -1 ? "Unlimited" : (user?.credits ?? 0)}
              </div>
            </div>
            <Button asChild className="rounded-full bg-primary hover:bg-[#FF7043] text-white h-11 px-6" data-testid="new-list-btn">
              <Link to="/new"><Plus className="w-4 h-4 mr-1.5" /> New lead list</Link>
            </Button>
            {user?.credits !== -1 && user?.credits <= 0 && (
              <Button asChild variant="outline" className="rounded-full h-11 px-6 border-primary/40 text-primary hover:bg-primary/10" data-testid="buy-credits-btn">
                <Link to="/pricing">Upgrade plan</Link>
              </Button>
            )}
          </div>
        </div>

        {lists === null ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : lists.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-16 text-center">
            <FileText className="w-8 h-8 text-muted-foreground mx-auto" />
            <div className="mt-4 text-lg font-medium">No lead lists yet</div>
            <div className="text-sm text-muted-foreground mt-1">Create your first list to see leads here.</div>
            <Button asChild className="rounded-full bg-primary hover:bg-[#FF7043] text-white mt-6" data-testid="empty-new-list-btn">
              <Link to="/new">Create your first list</Link>
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="lists-grid">
            {lists.map((l) => (
              <Link
                key={l.id}
                to={l.status === "generating" ? `/lists/${l.id}/progress` : `/lists/${l.id}`}
                className="rounded-xl border border-border bg-[#121214] p-6 hover:-translate-y-0.5 transition-transform duration-200 ease-out"
                data-testid={`list-card-${l.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{l.industry}</div>
                  <StatusBadge status={l.status} />
                </div>
                <div className="mt-3 text-xl font-medium tracking-tight line-clamp-1">{l.business_name}</div>
                <div className="mt-1 text-sm text-muted-foreground line-clamp-1">{l.location}</div>
                <div className="mt-6 flex items-center justify-between text-sm">
                  <div className="text-muted-foreground">{l.count} leads requested</div>
                  <Badge variant="outline" className="rounded-full border-border">{l.leads_count} generated</Badge>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
